/**
 * Custom fields manager.
 *
 * Repointed onto the org-scoped custom-field catalog (OrgCustomField) after the
 * legacy per-project Feature-20 system was retired. Fields are defined per
 * organization + entity type (default "task"); a stable machine `key` is
 * derived from the label. Supports text, number, date, select, multiselect.
 *
 * Drag-to-reorder persists `display_order` via per-field PATCH (the org catalog
 * has no bulk reorder endpoint).
 */
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, GripVertical, Loader2 } from 'lucide-react'
import {
  orgCustomFieldsService,
  type OrgCustomField,
  type OrgCustomFieldEntityType,
  type OrgCustomFieldType,
} from '../../services/orgCustomFields'
import { useTranslation } from '../../i18n/I18nContext'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Switch } from '../ui/switch'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import type { ApiErrorLike } from '../../utils/apiError'

const FIELD_TYPES: { value: OrgCustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
]

const FIELD_TYPE_LABELS: Record<string, string> = Object.fromEntries(FIELD_TYPES.map(f => [f.value, f.label]))

/** Slugify a label into a stable machine key (lowercase, underscores). */
function toKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64)
}

interface Props {
  /** Org that owns the catalog. When null (single-tenant) the catalog is
   *  global; the backend accepts a null organization_id. */
  organizationId: number | null
  /** Which entity these custom fields attach to. */
  entityType?: OrgCustomFieldEntityType
}

export function CustomFieldsManager({ organizationId, entityType = 'task' }: Props) {
  const { t } = useTranslation()
  const [fields, setFields] = useState<OrgCustomField[]>([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dragging, setDragging] = useState<number | null>(null)

  const [form, setForm] = useState({
    name: '',
    field_type: 'text' as OrgCustomFieldType,
    options: '',
    required: false,
  })
  const [formError, setFormError] = useState('')

  // The org endpoints require an org id in the path; -1 is rejected, so we
  // can't load until we know it. Treat null as "no org" → use 0 sentinel only
  // if the backend supports it; otherwise show an inert empty state.
  const orgId = organizationId ?? 0

  useEffect(() => {
    if (organizationId == null) { setLoading(false); return }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, entityType])

  async function load() {
    setLoading(true)
    try {
      const data = await orgCustomFieldsService.list(orgId, entityType)
      setFields([...data].sort((a, b) => a.display_order - b.display_order))
    } catch {
      toast.error('Failed to load custom fields')
    } finally {
      setLoading(false)
    }
  }

  function openDialog() {
    setForm({ name: '', field_type: 'text', options: '', required: false })
    setFormError('')
    setShowDialog(true)
  }

  async function handleCreate() {
    if (!form.name.trim()) {
      setFormError('Field name is required')
      return
    }
    const key = toKey(form.name)
    if (!key) {
      setFormError('Field name must contain letters or numbers')
      return
    }
    setSaving(true)
    try {
      const options =
        form.field_type === 'select' || form.field_type === 'multiselect'
          ? form.options
              .split(',')
              .map(o => o.trim())
              .filter(Boolean)
              .map(o => ({ value: toKey(o) || o, label: o }))
          : undefined

      const created = await orgCustomFieldsService.create(orgId, {
        entity_type: entityType,
        key,
        label: form.name.trim(),
        field_type: form.field_type,
        options,
        required: form.required,
        display_order: fields.length,
      })
      setFields(prev => [...prev, created])
      setShowDialog(false)
      toast.success('Custom field created')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to create field')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(fieldId: number) {
    try {
      await orgCustomFieldsService.remove(orgId, fieldId)
      setFields(prev => prev.filter(f => f.id !== fieldId))
      toast.success('Field deleted')
    } catch {
      toast.error('Failed to delete field')
    }
  }

  async function handleToggleRequired(field: OrgCustomField) {
    try {
      const updated = await orgCustomFieldsService.update(orgId, field.id, { required: !field.required })
      setFields(prev => prev.map(f => (f.id === updated.id ? updated : f)))
    } catch {
      toast.error('Failed to update field')
    }
  }

  // Simple drag-to-reorder (mouse events; no library)
  function handleDragStart(index: number) { setDragging(index) }
  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault()
    if (dragging === null || dragging === index) return
    const reordered = [...fields]
    const [moved] = reordered.splice(dragging, 1)
    reordered.splice(index, 0, moved)
    setFields(reordered)
    setDragging(index)
  }
  async function handleDragEnd() {
    setDragging(null)
    try {
      // Persist new order by patching each field's display_order.
      await Promise.all(
        fields.map((f, i) =>
          f.display_order === i
            ? Promise.resolve()
            : orgCustomFieldsService.update(orgId, f.id, { display_order: i })
        )
      )
    } catch {
      toast.error('Failed to save field order')
      load()
    }
  }

  if (organizationId == null) {
    return (
      <section aria-labelledby="custom-fields-heading">
        <h2 id="custom-fields-heading" className="text-base font-semibold mb-2">{t('customFields.title')}</h2>
        <p className="text-sm text-muted-foreground py-4 text-center">
          Custom fields are managed at the organization level.
        </p>
      </section>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" aria-label="Loading custom fields">
        <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
        <span className="sr-only">{t('common.loading')}</span>
      </div>
    )
  }

  return (
    <section aria-labelledby="custom-fields-heading">
      <div className="flex items-center justify-between mb-4">
        <h2 id="custom-fields-heading" className="text-base font-semibold">{t('customFields.title')}</h2>
        <Button onClick={openDialog} size="sm" className="min-h-[44px] min-w-[44px]">
          <Plus className="size-4 mr-1" aria-hidden />
          {t('customFields.addField')}
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          {t('customFields.empty')}
        </p>
      ) : (
        <ul className="space-y-2" aria-label="Custom fields list">
          {fields.map((field, index) => (
            <li
              key={field.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={e => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 rounded-lg border bg-card p-3 cursor-default ${dragging === index ? 'opacity-50' : ''}`}
            >
              <button
                aria-label="Drag to reorder"
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] flex items-center justify-center"
                tabIndex={-1}
              >
                <GripVertical className="size-4" aria-hidden />
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">{field.label}</span>
                  <Badge variant="secondary" className="text-xs shrink-0">
                    {FIELD_TYPE_LABELS[field.field_type] ?? field.field_type}
                  </Badge>
                  {field.required && (
                    <Badge variant="outline" className="text-xs text-destructive border-destructive shrink-0">
                      {t('common.required')}
                    </Badge>
                  )}
                </div>
                {field.options && field.options.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    Options: {field.options.map(o => o.label).join(', ')}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <Switch
                    id={`required-${field.id}`}
                    checked={field.required}
                    onCheckedChange={() => handleToggleRequired(field)}
                    aria-label={`${field.label} required`}
                  />
                  <Label htmlFor={`required-${field.id}`} className="text-xs sr-only">
                    {t('common.required')}
                  </Label>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 min-h-[44px] min-w-[44px]"
                  onClick={() => handleDelete(field.id)}
                  aria-label={`Delete ${field.label} field`}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="field-name">
                Field name <span aria-hidden className="text-destructive">*</span>
              </Label>
              <Input
                id="field-name"
                value={form.name}
                onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFormError('') }}
                placeholder="e.g. Trade, Permit Number"
                className="mt-1 min-h-[44px]"
                aria-required="true"
                aria-invalid={!!formError}
                aria-describedby={formError ? 'field-name-error' : undefined}
              />
              {formError && (
                <p id="field-name-error" className="text-sm text-destructive mt-1" role="alert">{formError}</p>
              )}
            </div>

            <div>
              <Label htmlFor="field-type">Type</Label>
              <Select value={form.field_type} onValueChange={v => setForm(f => ({ ...f, field_type: v as OrgCustomFieldType }))}>
                <SelectTrigger id="field-type" className="mt-1 min-h-[44px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(ft => (
                    <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(form.field_type === 'select' || form.field_type === 'multiselect') && (
              <div>
                <Label htmlFor="field-options">Options (comma-separated)</Label>
                <Input
                  id="field-options"
                  value={form.options}
                  onChange={e => setForm(f => ({ ...f, options: e.target.value }))}
                  placeholder="Option A, Option B, Option C"
                  className="mt-1 min-h-[44px]"
                />
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch
                id="field-required"
                checked={form.required}
                onCheckedChange={v => setForm(f => ({ ...f, required: v }))}
              />
              <Label htmlFor="field-required">Required</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="min-h-[44px]">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="min-h-[44px]">
              {saving ? <Loader2 className="size-4 animate-spin mr-1" aria-hidden /> : null}
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
